import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils/styles';

interface BreadcrumbsProps {
  items: {
    label: string;
    href: string;
  }[];
  className?: string;
}

export const Breadcrumbs = React.memo(function Breadcrumbs({ 
  items,
  className,
}: BreadcrumbsProps) {
  if (items.length === 0) return null;

  return (
    <nav
      className={cn(
        'flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400',
        className
      )}
      aria-label="Breadcrumbs"
    >
      <ol className="flex items-center space-x-2">
        {/* Home link */}
        <li>
          <Link
            to="/"
            className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Home
          </Link>
        </li>

        {/* Breadcrumb items */}
        {items.map((item, index) => (
          <React.Fragment key={item.href}>
            <li className="flex items-center">
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </li>
            <li>
              {index === items.length - 1 ? (
                // Current page (not clickable)
                <span
                  className="font-medium text-gray-900 dark:text-gray-100"
                  aria-current="page"
                >
                  {item.label}
                </span>
              ) : (
                // Navigation link
                <Link
                  to={item.href}
                  className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  {item.label}
                </Link>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
});

/**
 * Example usage:
 * 
 * function DashboardPage() {
 *   const { breadcrumbs } = useNavigation({ userRole: 'admin' });
 * 
 *   return (
 *     <div>
 *       <Breadcrumbs items={breadcrumbs} className="mb-4" />
 *       <h1>Dashboard</h1>
 *     </div>
 *   );
 * }
 */
